import React, { useState } from 'react';
import { Box, Button, Collapse, Divider, Stack, Typography } from '@mui/material';
import { Extension } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { PLAN_REQUIREMENTS_QUERY_KEY } from './planLock/usePlanRequirements';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog, InlineMessage, LoadingState, SectionCard, StatusChip } from './ui';

export interface PlanAddonState {
  key: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: 'monthly' | 'yearly' | 'lifetime';
  unlocks: string | null;
  includedInPlan: boolean;
  availableToBuy: boolean;
  purchasable: boolean;
  purchased: boolean;
  comingSoon: boolean;
  partiallyLive: boolean;
}

interface PlanAddonsResponse {
  tier: string | null;
  hasStripeSubscription: boolean;
  addons: PlanAddonState[];
}

export const PLAN_ADDONS_QUERY_KEY = ['billing-plan-addons'] as const;

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
};

/** True when the add-on cannot be bought, is not included, and is not already on the subscription. */
const isUnavailableOnPlan = (addon: PlanAddonState) =>
  !addon.purchased && !addon.includedInPlan && !addon.purchasable && !addon.availableToBuy;

/**
 * Jordan buys and drops the a la carte add-ons from the pricing sheet without
 * contacting sales. An add-on the plan already includes is shown as included
 * and can never be bought; one the tier does not sell says so. Every purchase
 * is one line on the store's existing Stripe subscription.
 */
export const PlanAddonsCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [pendingRemoval, setPendingRemoval] = useState<PlanAddonState | null>(null);
  const [showAddons, setShowAddons] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: PLAN_ADDONS_QUERY_KEY,
    queryFn: async (): Promise<PlanAddonsResponse> => {
      const response = await makeApiCall('/billing/addons', { headers: await getAuthHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || t('billing.addons.loadError'));
      }
      return body.data as PlanAddonsResponse;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: PLAN_ADDONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['my-subscription'] });
    // Buying or dropping an add-on changes which plan features the store has,
    // so a greyed nav row or page unlocks on the same screen.
    queryClient.invalidateQueries({ queryKey: PLAN_REQUIREMENTS_QUERY_KEY });
  };

  const buy = useMutation({
    mutationFn: async (key: string) => {
      const response = await makeApiCall('/billing/addons', {
        method: 'POST',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || t('billing.addons.addError'));
      }
      return body.data as PlanAddonsResponse;
    },
    onSuccess: () => {
      showToast(t('billing.addons.added'), 'success');
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const drop = useMutation({
    mutationFn: async (key: string) => {
      const response = await makeApiCall(`/billing/addons/${key}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || t('billing.addons.removeError'));
      }
      return body.data as PlanAddonsResponse;
    },
    onSuccess: () => {
      showToast(t('billing.addons.removed'), 'success');
      setPendingRemoval(null);
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const busy = buy.isPending || drop.isPending;

  const renderAddon = (addon: PlanAddonState) => (
    <Box key={addon.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', py: 1.5 }}>
      <Stack spacing={0.25} sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>
            {addon.name}
          </Typography>
          {/* Already purchased: keep price, Active, and Remove. Chip sits by the name. */}
          {addon.comingSoon && addon.purchased && (
            <StatusChip label={t('billing.addons.comingSoon')} tone="neutral" />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {addon.description}
        </Typography>
        {addon.partiallyLive && !addon.comingSoon && (
          <Typography variant="caption" color="text.secondary">
            {t(`billing.addons.partialNote.${addon.key}`)}
          </Typography>
        )}
      </Stack>
      {addon.comingSoon && !addon.purchased ? (
        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
          <StatusChip label={t('billing.addons.comingSoon')} tone="neutral" />
        </Box>
      ) : (
        <>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 110 }}>
            {formatMoney(addon.price, addon.currency)}
            {addon.interval === 'yearly' ? t('billing.addons.perYear') : t('billing.addons.perMonth')}
          </Typography>
          <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {addon.purchased ? (
              <>
                <StatusChip
                  label={
                    addon.includedInPlan ? t('billing.addons.includedInPlan') : t('billing.addons.active')
                  }
                  tone={addon.includedInPlan ? 'positive' : 'info'}
                />
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => setPendingRemoval(addon)}
                >
                  {t('billing.addons.remove')}
                </Button>
              </>
            ) : addon.includedInPlan ? (
              <StatusChip label={t('billing.addons.includedInPlan')} tone="positive" />
            ) : addon.purchasable ? (
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={() => buy.mutate(addon.key)}
              >
                {t('billing.addons.add')}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {addon.availableToBuy ? t('billing.addons.contactSales') : t('billing.addons.notOnPlan')}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const renderBody = (addons: PlanAddonState[]) => {
    const live = addons.filter((addon) => !addon.comingSoon);
    const comingSoon = addons.filter((addon) => addon.comingSoon);
    return (
      <>
        {live.length > 0 && (
          <Stack divider={<Divider flexItem />} spacing={0}>
            {live.map(renderAddon)}
          </Stack>
        )}
        {comingSoon.length > 0 && (
          <Box sx={{ mt: live.length > 0 ? 2 : 0 }}>
            <Typography variant="overline" color="text.secondary">
              Coming soon
            </Typography>
            <Stack divider={<Divider flexItem />} spacing={0}>
              {comingSoon.map(renderAddon)}
            </Stack>
          </Box>
        )}
      </>
    );
  };

  // A plan that sells none of the add-ons (e.g. Free Preview) gets one line
  // and a toggle instead of a full list of "Not available on your plan".
  const collapseAll = Boolean(data && data.addons.length > 0 && data.addons.every(isUnavailableOnPlan));

  return (
    <SectionCard title={t('billing.addons.title')} leading={<Extension color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('billing.addons.loadError')}</InlineMessage>
      ) : collapseAll ? (
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Add-ons are available on paid plans
            </Typography>
            <Button
              size="small"
              onClick={() => setShowAddons((open) => !open)}
              aria-expanded={showAddons}
            >
              {showAddons ? 'Hide add-ons' : `Show add-ons (${data.addons.length})`}
            </Button>
          </Box>
          <Collapse in={showAddons} unmountOnExit>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('billing.addons.intro')}
              </Typography>
              {renderBody(data.addons)}
            </Stack>
          </Collapse>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('billing.addons.intro')}
          </Typography>
          {!data.hasStripeSubscription && (
            <InlineMessage severity="info">{t('billing.addons.noSubscription')}</InlineMessage>
          )}
          {renderBody(data.addons)}
        </Stack>
      )}
      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title={t('billing.addons.removeTitle')}
        message={t('billing.addons.removeMessage', { name: pendingRemoval?.name || '' })}
        confirmLabel={t('billing.addons.remove')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        loading={drop.isPending}
        onConfirm={() => pendingRemoval && drop.mutate(pendingRemoval.key)}
        onClose={() => setPendingRemoval(null)}
      />
    </SectionCard>
  );
};

export default PlanAddonsCard;
